const VALID_FILE_TYPES = new Set([
  'image/png', 'image/gif', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/webp',
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska',
]);

export const isValidFileType = (mimeType: string): boolean => VALID_FILE_TYPES.has(mimeType.toLowerCase());

export const cleanHtml = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('script, style, meta, link').forEach((el) => el.remove());

  // Google Docs uses inline styles instead of semantic tags. Convert to
  // <strong>/<em> before stripping styles so formatting is preserved.
  doc.querySelectorAll('span').forEach((span) => {
    const isBold = span.style.fontWeight === '700' || span.style.fontWeight === 'bold';
    const isItalic = span.style.fontStyle === 'italic';
    if (!isBold && !isItalic) return;

    let replacement: Node = doc.createTextNode('');
    const inner = span.innerHTML;

    if (isBold && isItalic) {
      const em = doc.createElement('em');
      em.innerHTML = inner;
      const strong = doc.createElement('strong');
      strong.appendChild(em);
      replacement = strong;
    } else if (isBold) {
      const strong = doc.createElement('strong');
      strong.innerHTML = inner;
      replacement = strong;
    } else {
      const em = doc.createElement('em');
      em.innerHTML = inner;
      replacement = em;
    }

    span.replaceWith(replacement);
  });

  doc.querySelectorAll('b[id^="docs-internal-guid"]').forEach((b) => {
    b.replaceWith(...Array.from(b.childNodes));
  });

  const allElements = doc.body.querySelectorAll('*');
  allElements.forEach((el) => {
    const attrsToRemove: string[] = [];
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('on') || attr.name === 'style' || attr.name === 'class') {
        attrsToRemove.push(attr.name);
      }
    }
    attrsToRemove.forEach((attr) => el.removeAttribute(attr));
  });

  return doc.body.innerHTML;
};

export const extractFilesFromClipboard = (clipboardData: DataTransfer): File[] => {
  const files: File[] = [];
  const seenSizes = new Set<number>();

  for (const file of Array.from(clipboardData.files)) {
    if (isValidFileType(file.type) && !seenSizes.has(file.size)) {
      seenSizes.add(file.size);
      files.push(file);
    }
  }

  if (files.length === 0) {
    for (const item of Array.from(clipboardData.items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && isValidFileType(file.type) && !seenSizes.has(file.size)) {
          seenSizes.add(file.size);
          files.push(file);
        }
      }
    }
  }

  return files;
};

export const removeImagesFromHtml = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('img').forEach((img) => img.remove());
  return doc.body.innerHTML;
};

const MARKDOWN_PATTERNS = [
  /\*\*.+?\*\*/,
  /__(.+?)__/,
  /\*[^*\n]+\*/,
  /~~.+?~~/,
  /`.+?`/,
  /^#+\s/m,
  /^[ \t]*[-*][ \t]+/m,
  /^[ \t]*\d+[.)]\s+/m,
  /\[.+?\]\(.+?\)/,
  /!\[.*?\]\(.+?\)/,
  /^>/m,
  /^(---|\*\*\*|___)$/m,
];

export const containsMarkdown = (text: string): boolean =>
  MARKDOWN_PATTERNS.some((pattern) => pattern.test(text));

const RICH_FORMATTING_TAGS = ['strong', 'b', 'em', 'i', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

export const hasRichFormatting = (html: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return RICH_FORMATTING_TAGS.some((tag) => doc.body.querySelector(tag) !== null);
};

const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/g;

export const autoLinkUrls = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  let node: Text | null;
  while ((node = walker.nextNode() as Text | null)) {
    if (node.parentElement?.closest('a')) continue;
    if (URL_REGEX.test(node.textContent || '')) {
      textNodes.push(node);
    }
    URL_REGEX.lastIndex = 0;
  }

  for (const textNode of textNodes) {
    const content = textNode.textContent || '';
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;

    URL_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_REGEX.exec(content)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(doc.createTextNode(content.slice(lastIndex, match.index)));
      }
      const anchor = doc.createElement('a');
      anchor.href = match[0];
      anchor.target = '_blank';
      anchor.textContent = match[0];
      fragment.appendChild(anchor);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      fragment.appendChild(doc.createTextNode(content.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  }

  return doc.body.innerHTML;
};
